import React from 'react'
import { usePushSubscription } from '../hooks/usePushSubscription'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { Button, Modal } from '@/shared/ui'
import { isPushEnabledForAll } from '../services/webpushClient'
import { useToast } from '@/shared/ui/Toast'
import { isMobileDevice } from '../utils/device'

export default function PushOptInModal() {
  const { user, profile } = useAuth()
  const { status, loading, pwaInstalled, enable, getOptOut, forceReoptin, shouldPrompt } = usePushSubscription()
  const { add: toast } = useToast()

  const [activating, setActivating] = React.useState(false)
  const [result, setResult] = React.useState<'idle' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null)
  const [chooseSnooze, setChooseSnooze] = React.useState(false)
  const [open, setOpen] = React.useState(false)

  function snoozeActive(): boolean {
    try {
      const v = Number(localStorage.getItem('webpush.optin.snooze_until') || '')
      return Number.isFinite(v) && v > Date.now()
    } catch { return false }
  }

  React.useEffect(() => {
    if (loading) return
    if (!user?.id) return
    if (!isMobileDevice()) return
    if (typeof Notification === 'undefined') return
    const checkOpen = async () => {
      if (!pwaInstalled) return
      if (forceReoptin || shouldPrompt) { setOpen(true); return }
      if (profile?.role !== 'master') {
        const enabledAll = await isPushEnabledForAll()
        if (!enabledAll) return
      }
      if (getOptOut()) { setOpen(true); return }
      if (snoozeActive()) return
      if (Notification.permission === 'granted') return
      if (Notification.permission === 'denied') return
      setOpen(true)
    }
    checkOpen()
  }, [loading, pwaInstalled, profile?.role, status, user?.id, forceReoptin, shouldPrompt])

  async function onEnable() {
    if (activating) return
    setActivating(true)
    try {
      const ok = await enable()
      if (ok) {
        setResult('success')
        setErrorMsg(null)
        try { localStorage.removeItem('webpush.optin.snooze_until') } catch {}
      } else {
        const perm = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported'
        const msg = perm === 'denied'
          ? 'Permissão negada no sistema. Ative nas configurações do iPhone e retorne.'
          : 'Não foi possível ativar agora. Verifique sua conexão e tente novamente.'
        setErrorMsg(msg)
        setResult('error')
        toast({ title: 'Notificações', description: 'Falha ao ativar', variant: 'error' })
      }
    } finally {
      setActivating(false)
    }
  }

  function onDismiss() { setChooseSnooze(true) }

  function setSnooze(days: number) {
    const ms = days * 24 * 60 * 60 * 1000
    try { localStorage.setItem('webpush.optin.snooze_until', String(Date.now() + ms)) } catch {}
    setChooseSnooze(false)
    setOpen(false)
  }

  if (!open) return null

  return (
    <Modal open={open} onOpenChange={setOpen} dismissible={false}>
      <div className="space-y-3">
        {!chooseSnooze ? (
          <>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold">Ativar notificações</h3>
              {forceReoptin && (
                <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium text-primary">Atualização feita</span>
              )}
            </div>
            {result === 'idle' && (
              <>
                {(forceReoptin || shouldPrompt) && (
                  <p className="text-sm text-foreground/80">Sua notificação precisa ser atualizada. Clique no botão abaixo para renovar sua inscrição e continuar recebendo alertas no seu celular.</p>
                )}
                <p className="text-sm text-foreground/80">Receba oportunidades confirmadas no seu celular.</p>
                <div className="pt-3 flex items-center justify-end gap-3">
                  <Button variant="ghost" className="h-11" onClick={onDismiss} disabled={activating}>Depois</Button>
                  <Button className="h-11" onClick={onEnable} disabled={activating}>{activating ? 'Ativando...' : 'Ativar notificações'}</Button>
                </div>
              </>
            )}
            {result === 'success' && (
              <>
                <p className="text-sm text-foreground/80">Pronto! Notificações ativadas neste dispositivo.</p>
                <div className="pt-3 flex items-center justify-end gap-3">
                  <Button className="h-11" onClick={() => setOpen(false)}>OK</Button>
                </div>
              </>
            )}
            {result === 'error' && (
              <>
                <p className="text-sm text-destructive">{errorMsg || 'Não foi possível ativar. Tente novamente.'}</p>
                <div className="pt-3 flex items-center justify-between gap-3">
                  <Button variant="outline" className="h-11" onClick={onDismiss}>Depois</Button>
                  <Button className="h-11" onClick={onEnable} disabled={activating}>{activating ? 'Ativando...' : 'Tentar novamente'}</Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h3 className="text-base font-semibold">Lembrar depois</h3>
            <p className="text-sm text-foreground/80">Quando devo lembrar você de ativar as notificações?</p>
            <div className="pt-3 space-y-2">
              <Button variant="outline" className="w-full h-11" onClick={() => setChooseSnooze(false)}>Voltar</Button>
              <Button variant="outline" className="w-full h-11" onClick={() => setSnooze(1)}>Em 1 dia</Button>
              <Button variant="outline" className="w-full h-11" onClick={() => setSnooze(3)}>Em 3 dias</Button>
              <Button variant="outline" className="w-full h-11" onClick={() => setSnooze(7)}>Em 7 dias</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
